import React from 'react';
import { Badge, Flex, Text } from '@mantine/core';
import { generatePath } from 'react-router-dom';
import { UserInitialsBadge } from '@dealstack/frontend/shared/components/UserInitialsBadge/UserInitialsBadge.tsx';
import { EditableTileContainer } from '@dealstack/frontend/shared/components/EditableTileContainer/EditableTileContainer.tsx';
import { StyledLink } from '@dealstack/frontend/shared/components/UserRowWithActions/styled.tsx';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { RelatedPersonRow } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/RelatedPersonsSection.types.ts';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

interface Props {
	row: RelatedPersonRow;
	dealId: string;
	typeLabel: string;
	hasEditPermission: boolean;
	onDelete: () => void;
}

export const RelatedPersonRowItem = ({
	row,
	dealId,
	typeLabel,
	hasEditPermission,
	onDelete,
}: Props) => {
	const [firstName, lastName] = (row.displayName ?? '').split(' ');
	const userLink = generatePath(
		`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`,
		{ dealId: dealId ?? '', personId: row.personId }
	);
	// Not personId alone: the same person can hold two relation types on one entity.
	const testIdSuffix = `${row.relationType}_${row.personId}`;

	return (
		<EditableTileContainer
			hasEditPermission={hasEditPermission}
			onDeleteClick={onDelete}
			dataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.CAP_RELATED_PERSONS_ROW,
				testIdSuffix
			)}
			deleteBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.CAP_RELATED_PERSONS_ROW_DELETE_BUTTON,
				testIdSuffix
			)}
		>
			<Flex direction="column" gap="0.5rem">
				<Flex align="center" gap="0.5rem">
					<UserInitialsBadge firstName={firstName} lastName={lastName} textVariant="xs" />
					<Flex direction="column" w="100%">
						<StyledLink to={userLink}>
							<Text
								variant="h6"
								fw={600}
								lineClamp={1}
								truncate
								style={{ wordBreak: 'break-all' }}
							>
								{row.displayName}
							</Text>
						</StyledLink>
						{row.email && (
							<Text
								variant="sm"
								c="dark.0"
								lineClamp={1}
								truncate
								style={{ wordBreak: 'break-all' }}
							>
								{row.email}
							</Text>
						)}
					</Flex>
				</Flex>
				<Badge
					size="sm"
					variant="outline"
					fw={400}
					color="gray.4"
					data-testid={makeComplexDataTestId(
						TEST_ELEMENT_IDS.CAP_RELATED_PERSONS_ROW_TYPE_BADGE,
						testIdSuffix
					)}
				>
					<Text c="dark.8" lineClamp={1} truncate style={{ wordBreak: 'break-all' }}>
						{typeLabel}
					</Text>
				</Badge>
			</Flex>
		</EditableTileContainer>
	);
};
